import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Container, Icon, PageHero, Reveal, SectionHeading, ArrowFwd } from "../components/shared/Shared";
import { useLang } from "../context/LanguageContext";
import { IndustriesGrid, Resources } from "./HomeSections";

const Capabilities = ({ title, subtitle }) => {
  const { t, c } = useLang();
  return (
    <section className="bg-slate-50 py-24" data-testid="capabilities-section">
      <Container>
        <SectionHeading eyebrow={t.detail.capEyebrow} title={title || t.detail.capTitle} description={subtitle || t.detail.capDesc} />
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {c.OFFERING_CAPABILITIES.map((cap, i) => (
            <Reveal key={cap.title} delay={(i % 3) * 90} className="card-hover group rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-100">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal transition-colors group-hover:bg-brand-teal group-hover:text-white"><Icon name={cap.icon} className="h-6 w-6" /></span>
              <h3 className="mt-6 font-heading text-xl font-bold leading-snug text-brand-navy">{cap.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{cap.description}</p>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

const SideNav = ({ items, current, base, heading }) => (
  <aside className="lg:sticky lg:top-32">
    <div className="rounded-2xl border border-slate-200 bg-white p-2">
      <p className="px-4 pb-2 pt-3 text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">{heading}</p>
      {items.map((i) => (
        <Link key={i.slug} to={`${base}/${i.slug}`} className={`flex items-center justify-between rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${i.slug === current ? "bg-brand-teal text-white" : "text-slate-700 hover:bg-slate-50 hover:text-brand-teal"}`}>
          {i.title}<ArrowFwd className="h-4 w-4 opacity-60" />
        </Link>
      ))}
    </div>
  </aside>
);

export const Offering = ({ onQuote }) => {
  const { slug } = useParams();
  const { t, c } = useLang();
  const item = c.ALL_OFFERINGS.find((o) => o.slug === slug);
  if (!item) return <Navigate to="/" replace />;
  const group = c.OFFERING_GROUPS.find((g) => g.group === item.group);
  const d = t.detail;
  return (
    <>
      <PageHero title={item.title} subtitle={item.group} crumbs={[{ label: d.offering }, { label: item.title }]} image={c.IMAGES.datacenter} />
      <section className="py-20" data-testid="offering-detail">
        <Container className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full bg-brand-orange/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange"><Icon name={group.icon} className="h-4 w-4" />{item.group}</span>
              <h2 className="mt-6 font-heading text-3xl font-bold text-brand-navy sm:text-4xl">{item.title}</h2>
              <p className="mt-6 text-lg leading-relaxed text-slate-600">{item.description}</p>
              <p className="mt-4 leading-relaxed text-slate-600">{d.intro(item.title)}</p>
            </Reveal>
            <Reveal delay={100} className="mt-10 grid gap-4 sm:grid-cols-2">
              {d.steps.map((p) => <div key={p} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-4"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-teal" /><span className="text-sm font-medium text-slate-700">{p}</span></div>)}
            </Reveal>
            <Reveal delay={150} className="mt-10 flex flex-wrap gap-4">
              <button type="button" onClick={onQuote} className="btn-primary bg-brand-orange text-white hover:bg-orange-600" data-testid="offering-quote-button">{d.proposal} <ArrowFwd className="h-4 w-4" /></button>
              <Link to="/contact" className="btn-outline">{d.talk}</Link>
            </Reveal>
          </div>
          <div className="lg:col-span-4"><SideNav items={group.items} current={slug} base="/offerings" heading={group.group} /></div>
        </Container>
      </section>
      <Capabilities />
      <Resources />
    </>
  );
};

export const Industry = ({ onQuote }) => {
  const { slug } = useParams();
  const { t, c } = useLang();
  const ind = c.INDUSTRIES.find((i) => i.slug === slug);
  if (!ind) return <Navigate to="/" replace />;
  const d = t.detail;
  return (
    <>
      <PageHero title={ind.title} subtitle={ind.summary} crumbs={[{ label: d.industries }, { label: ind.title }]} image={ind.image} />
      <section className="py-20" data-testid="industry-detail">
        <Container className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Reveal className="overflow-hidden rounded-3xl shadow-xl shadow-brand-navy/10"><img src={ind.image} alt={ind.title} className="aspect-[16/8] w-full object-cover" /></Reveal>
            <Reveal delay={80}>
              <h2 className="mt-10 font-heading text-3xl font-bold text-brand-navy sm:text-4xl">{ind.title}</h2>
              {ind.description.map((p) => <p key={p.slice(0, 30)} className="mt-5 text-lg leading-relaxed text-slate-600">{p}</p>)}
            </Reveal>
            <Reveal delay={150} className="mt-10 flex flex-wrap gap-4">
              <button type="button" onClick={onQuote} className="btn-primary bg-brand-orange text-white hover:bg-orange-600" data-testid="industry-quote-button">{t.nav.quote} <ArrowFwd className="h-4 w-4" /></button>
              <Link to="/contact" className="btn-outline">{d.talk}</Link>
            </Reveal>
          </div>
          <div className="lg:col-span-4"><SideNav items={c.INDUSTRIES} current={slug} base="/industries" heading={d.industries} /></div>
        </Container>
      </section>
      <Capabilities title={c.OFFERING_CAPABILITIES[0].title} subtitle={c.OFFERING_CAPABILITIES[0].description} />
      <IndustriesGrid compact />
    </>
  );
};
